import type { Client, PrintJob, PrintOptions, PrinterInfo, RenderOptions } from './types'

/**
 * 打印管理器
 * 负责管理打印任务队列、状态回调与与服务端交互
 */
export class PrintManager {
  private client: Client
  private renderer?: (tpl: any, data: any, opts: RenderOptions) => Promise<Uint8Array>
  private queue: PrintJob[] = []
  private running = false
  private defaults: PrintOptions = { copies: 1, concurrency: 3, width: 90, height: 110, format: 'png' }
  private statusHandlers: Set<(s: { type: string; data?: any }) => void> = new Set()

  constructor(client: Client, renderer?: (tpl: any, data: any, opts: RenderOptions) => Promise<Uint8Array>) {
    this.client = client
    this.renderer = renderer
    this.client.on('message', (msg: any) => {
      if (!msg || !msg.type) return
      const type = msg.type
      if (type === 'printers') this.emitStatus({ type: 'printers', data: msg.data as PrinterInfo[] })
      if (type === 'progress') this.emitStatus({ type: 'progress', data: msg.data })
      if (type === 'complete') this.emitStatus({ type: 'complete', data: msg.data })
      if (type === 'error') this.emitStatus({ type: 'error', data: msg.data })
    })
  }

  /**
   * 将打印任务加入队列
   */
  enqueue(job: PrintJob) {
    this.queue.push(job)
  }

  /**
   * 依次执行队列中的任务
   */
  async start(): Promise<void> {
    if (this.running) return
    this.running = true
    try {
      while (this.queue.length > 0 && this.running) {
        const job = this.queue.shift() as PrintJob
        await this.processJob(job)
      }
    } finally {
      this.running = false
    }
  }

  /** 暂停队列执行 */
  pause() { this.running = false }

  /** 清空队列 */
  clear() { this.queue = [] }

  /** 监听打印状态更新 */
  onStatus(handler: (status: { type: string; data?: any }) => void) { this.statusHandlers.add(handler) }

  /** 请求打印机列表 */
  listPrinters() { this.client.send('list_printers') }

  /** 设置默认打印选项 */
  setDefaults(opts: PrintOptions) { this.defaults = { ...this.defaults, ...opts } }

  /**
   * 处理单个打印任务
   * 若未提供图片则使用渲染器渲染模板
   */
  async processJob(job: PrintJob) {
    const printer = job.options?.printer || this.defaults.printer || ''
    const copies = job.options?.copies ?? this.defaults.copies ?? 1
    const width = job.options?.width ?? this.defaults.width ?? 90
    const height = job.options?.height ?? this.defaults.height ?? 110
    const concurrency = job.options?.concurrency ?? this.defaults.concurrency ?? 3
    const format = job.options?.format ?? this.defaults.format ?? 'png'

    let images = job.images
    if (!images || images.length === 0) {
      if (!this.renderer || !job.template || !job.data) throw new Error('renderer or template/data missing')
      const img = await this.renderer(job.template, job.data, { format })
      images = [img]
    }

    const payload = { images, printer, copies, options: { width, height }, concurrency }
    this.client.send('direct_print', payload)
  }

  /**
   * 直接打印图片数组（跳过渲染）
   */
  printImages(images: Uint8Array[], opts?: PrintOptions) {
    const job: PrintJob = { images, options: opts }
    this.enqueue(job)
  }

  /**
   * 分发状态到监听器
   */
  private emitStatus(s: { type: string; data?: any }) { for (const h of this.statusHandlers) { try { h(s) } catch {} } }
}
